import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'webcam',
  imports: [],
  templateUrl: './webcam.component.html',
  styleUrl: './webcam.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class WebcamComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('video', { static: true }) videoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: true }) canvasEl!: ElementRef<HTMLCanvasElement>;

  /** Emits when a snapshot is taken. Payload is a base64 data URL (image/jpeg). */
  @Output('imageCapture') imageCapture = new EventEmitter<string>();

  /** Observable that triggers a capture when it emits. */
  @Input() trigger?: Observable<any>;

  /** Preview size (CSS pixels). Optional. */
  @Input() width?: number;
  @Input() height?: number;

  private stream?: MediaStream;
  private triggerSub?: Subscription;
  private isReady = false;
  private pendingCapture = false;

  ngOnInit(): void {
    this.subscribeToTrigger();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.startCamera();
    await this.waitForVideoReady();
  }

  ngOnDestroy(): void {
    this.triggerSub?.unsubscribe();
    this.stopCamera();
  }

  private waitForVideoReady(): Promise<void> {
    const video = this.videoEl.nativeElement;

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA && video.videoWidth && video.videoHeight) {
      this.isReady = true;
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const onReady = () => {
        if (video.videoWidth && video.videoHeight) {
          this.isReady = true;
          video.removeEventListener('loadedmetadata', onReady);
          video.removeEventListener('canplay', onReady);
          resolve();
        }
      };
      video.addEventListener('loadedmetadata', onReady);
      video.addEventListener('canplay', onReady);
    });
  }

  private subscribeToTrigger(): void {
    this.triggerSub?.unsubscribe();
    if (this.trigger) {
      this.triggerSub = this.trigger.subscribe(async() => await this.capture());
    }
  }



  private async startCamera(): Promise<void> {
    try {
      // Use environment/back camera when available; falls back automatically.
      const constraints: MediaStreamConstraints = {
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      };
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      const video = this.videoEl.nativeElement;
      video.setAttribute('playsinline', 'true');
      video.srcObject = this.stream;
      await video.play().catch(() => { });
    } catch (err) {
      console.error('Failed to start camera:', err);
    }
  }

  private stopCamera(): void {
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = undefined;
  }

private async capture(): Promise<void> {
  // If not ready yet, queue one capture and exit.
  if (!this.isReady) {
    this.pendingCapture = true;
    // Try again once we're ready
    this.waitForVideoReady().then(() => {
      if (this.pendingCapture) {
        this.pendingCapture = false;
        this.capture();
      }
    });
    return;
  }
 
  const video = this.videoEl.nativeElement;
  const canvas = this.canvasEl.nativeElement;
 
  // Scale down to requested preview size if provided, else use the video’s intrinsic size.
  const targetW =  video.videoWidth;
  const targetH = video.videoHeight;
 
  canvas.width = targetW;
  canvas.height = targetH;
 
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
 
  // Draw scaled frame (this is much faster than exporting a full 12MP image)
  ctx.drawImage(video, 0, 0, targetW, targetH);
 
  // Prefer async toBlob on mobile to avoid UI jank
  if (canvas.toBlob) {
    canvas.toBlob(async (blob) => {
      if (!blob) {
        // Fallback if something odd happens
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        this.imageCapture.emit(this.sanitizeBase64(this.stripHeader(dataUrl)));
        return;
      }
      const base64 = await this.blobToBase64(blob);
      this.imageCapture.emit(this.sanitizeBase64(base64));
    }, 'image/jpeg', 0.92);
  } else {
    // Old fallback
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    this.imageCapture.emit(this.sanitizeBase64(this.stripHeader(dataUrl)));
  }
}
 
// helpers
stripHeader(dataUrl: string): string {
  const i = dataUrl.indexOf(',');
  return i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
}
 
blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(this.stripHeader(result));
    };
    reader.readAsDataURL(blob);
  });
}

private sanitizeBase64(data: string): string{
  if (!data){ return ''; }
  const cleaned = data.replace(/[^A-Za-z0-9+/=]/g, '');
  const remainder = cleaned.length % 4;
  if (remainder === 0){
    return cleaned;
  }
  return cleaned + '='.repeat(4 - remainder);
}




  /*private capture(): void {
    const video = this.videoEl.nativeElement;
    if (!video.videoWidth || !video.videoHeight) {
      // Not ready yet; try again on the next animation frame
      requestAnimationFrame(() => this.capture());
      return;
    }

    const canvas = this.canvasEl.nativeElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    // Emit a JPEG data URL; tweak quality if you like (0.0–1.0).
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const comma = dataUrl.indexOf(',');
    const payload = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    this.imageCapture.emit(payload);
  }*/
}
